import React from 'react';
import { GameId } from '../../../types/enums';
import { GameStats } from './types';
import { GiMeditation } from 'react-icons/gi';
import { PiBrainBold } from 'react-icons/pi';
import { HiPuzzlePiece } from 'react-icons/hi2';

interface Game {
  id: GameId;
  name: string;
  emoji: string | React.ReactNode;
  description: string;
  instruction: string;
  color: string;
  duration: string;
}

interface MindGamesData {
  calming: Game[];
  cognitive: Game[];
  frustration: Game[];
  puzzles: Game[];
}

interface Badge {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface MindGamesProps {
  mindGames: MindGamesData;
  gameStats: GameStats;
  userBadges: string[];
  availableBadges: Badge[];
  onGameSelect: (gameId: GameId, instruction: string) => void;
}

const MindGames: React.FC<MindGamesProps> = ({
  mindGames,
  gameStats,
  userBadges,
  availableBadges,
  onGameSelect,
}) => {
  // Filter out frustration games - only show calm activities
  const calmGames = {
    calming: mindGames.calming,
    cognitive: mindGames.cognitive,
    puzzles: mindGames.puzzles,
  };

  return (
    <div className="mt-12 sm:mt-16 relative z-10">
      <div className="text-center mb-8 sm:mb-10">
        <h3 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-2">
          Reset Tools
        </h3>
        <p className="text-base text-gray-600">Short, calm activities to reset your focus</p>
      </div>

      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 border border-orange-100/40 shadow-sm">
        {/* Game Categories - Only Calm Activities */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          {/* Calming Activities */}
          <div className="bg-orange-50/30 rounded-lg p-5 border border-orange-100/60">
            <div className="text-center mb-4">
              <div className="flex justify-center mb-2">
                <GiMeditation className="text-3xl text-orange-500" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-1">Calming</h4>
              <p className="text-xs text-gray-600">Reduce stress & anxiety</p>
            </div>
            <div className="space-y-2">
              {calmGames.calming.map((game) => (
                <button
                  key={game.id}
                  onClick={() => onGameSelect(game.id, game.instruction)}
                  className="w-full p-2.5 bg-white hover:bg-orange-50/50 rounded-lg text-left transition-all duration-200 border border-orange-100/60 hover:border-orange-200/80"
                >
                  <div className="flex items-center space-x-2">
                    {typeof game.emoji === 'string' ? (
                      <span className="text-lg">{game.emoji}</span>
                    ) : (
                      <span className="inline-flex items-center">{game.emoji}</span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 font-medium text-sm truncate">{game.name}</div>
                      <div className="text-gray-500 text-xs">{game.duration}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Cognitive Activities */}
          <div className="bg-orange-50/30 rounded-lg p-5 border border-orange-100/60">
            <div className="text-center mb-4">
              <div className="flex justify-center mb-2">
                <PiBrainBold className="text-3xl text-orange-500" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-1">Cognitive</h4>
              <p className="text-xs text-gray-600">Reset attention & focus</p>
            </div>
            <div className="space-y-2">
              {calmGames.cognitive.map((game) => (
                <button
                  key={game.id}
                  onClick={() => onGameSelect(game.id, game.instruction)}
                  className="w-full p-2.5 bg-white hover:bg-orange-50/50 rounded-lg text-left transition-all duration-200 border border-orange-100/60 hover:border-orange-200/80"
                >
                  <div className="flex items-center space-x-2">
                    {typeof game.emoji === 'string' ? (
                      <span className="text-lg">{game.emoji}</span>
                    ) : (
                      <span className="inline-flex items-center">{game.emoji}</span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 font-medium text-sm truncate">{game.name}</div>
                      <div className="text-gray-500 text-xs">{game.duration}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Puzzle Activities */}
          <div className="bg-orange-50/30 rounded-lg p-5 border border-orange-100/60">
            <div className="text-center mb-4">
              <div className="flex justify-center mb-2">
                <HiPuzzlePiece className="text-3xl text-orange-500" />
              </div>
              <h4 className="text-lg font-medium text-gray-900 mb-1">Puzzles</h4>
              <p className="text-xs text-gray-600">Calm your brain</p>
            </div>
            <div className="space-y-2">
              {calmGames.puzzles.map((game) => (
                <button
                  key={game.id}
                  onClick={() => onGameSelect(game.id, game.instruction)}
                  className="w-full p-2.5 bg-white hover:bg-orange-50/50 rounded-lg text-left transition-all duration-200 border border-orange-100/60 hover:border-orange-200/80"
                >
                  <div className="flex items-center space-x-2">
                    {typeof game.emoji === 'string' ? (
                      <span className="text-lg">{game.emoji}</span>
                    ) : (
                      <span className="inline-flex items-center">{game.emoji}</span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 font-medium text-sm truncate">{game.name}</div>
                      <div className="text-gray-500 text-xs">{game.duration}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MindGames;
